export function usePinchGesture(
  containerRef: MaybeRefOrGetter<HTMLElement | null>,
  {
    minScale,
    maxScale,
    currentScale,
    onZoom,
    onOriginChange,
  }: {
    minScale: MaybeRefOrGetter<number>;
    maxScale: MaybeRefOrGetter<number>;
    currentScale?: MaybeRefOrGetter<number>;
    onZoom: (scale: number) => void;
    onOriginChange: (xPercent: number, yPercent: number) => void;
  },
) {
  const pointers = new Map<number, PointerEvent>();
  let initialDistance: number | null = null;
  let initialScale: number | null = null;

  const onPointerDown = (e: PointerEvent) => {
    pointers.set(e.pointerId, e);
    toValue(containerRef)?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, e);

    if (pointers.size === 2 && toValue(containerRef)) {
      const [p1, p2] = Array.from(pointers.values());
      if (!p1 || !p2) return;

      const midX = (p1.clientX + p2.clientX) / 2;
      const midY = (p1.clientY + p2.clientY) / 2;
      const rect = toValue(containerRef)?.getBoundingClientRect();

      if (!rect) return;

      // Update transform-origin
      onOriginChange(
        ((midX - rect.left) / rect.width) * 100,
        ((midY - rect.top) / rect.height) * 100,
      );

      const distance = Math.hypot(
        p2.clientX - p1.clientX,
        p2.clientY - p1.clientY,
      );

      if (initialDistance === null) {
        initialDistance = distance;
        initialScale = null;
      } else if (initialScale === null) {
        // Use current scale if provided, otherwise use minimum scale
        initialScale = currentScale ? toValue(currentScale) : toValue(minScale);
      } else {
        const scaleChange = distance / initialDistance;
        const newScale = Math.min(
          toValue(maxScale),
          Math.max(toValue(minScale), (initialScale ?? 1) * scaleChange),
        );
        onZoom(newScale);
      }
    }
  };

  const onPointerUp = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) {
      initialDistance = null;
      initialScale = null;
    }
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
